import type {
    Field,
    GeneratorConfigV1,
    VisibilityRule,
} from '../../types/generator-config';
import {
    fieldTypeCanBeConditionSource,
    getFieldById,
    listAllFields,
    operatorLabel,
    operatorsForFieldType,
    type ConditionOperator,
} from '../../lib/configUtils';

interface ConditionBuilderProps {
    config: GeneratorConfigV1;
    rule: VisibilityRule | undefined;
    onChange: (next: VisibilityRule | undefined) => void;
    /** ID du field courant (pour l'exclure des sources) */
    excludeFieldId?: string;
}

export function ConditionBuilder({
    config,
    rule,
    onChange,
    excludeFieldId,
}: ConditionBuilderProps) {
    const fields = listAllFields(config)
        .filter((r) => fieldTypeCanBeConditionSource(r.field.type))
        .filter((r) => r.field.id !== excludeFieldId);

    const sourceField = rule?.fieldId ? getFieldById(config, rule.fieldId) : undefined;
    const currentOp = rule ? inferOperator(rule) : undefined;
    const ops = sourceField ? operatorsForFieldType(sourceField.type) : [];

    const handleSourceChange = (fieldId: string) => {
        if (!fieldId) {
            onChange(undefined);
            return;
        }
        const newSource = getFieldById(config, fieldId);
        if (!newSource) return;
        const firstOp = operatorsForFieldType(newSource.type)[0];
        if (!firstOp) return;
        onChange(buildRule(fieldId, firstOp, initialValueFor(newSource, firstOp)));
    };

    const handleOpChange = (op: ConditionOperator) => {
        if (!sourceField || !rule) return;
        onChange(buildRule(rule.fieldId, op, initialValueFor(sourceField, op)));
    };

    if (fields.length === 0) {
        return (
            <div className="cond-hint">
                <em>
                    Ajoutez d'abord un champ à choix (liste, chips, radio, nombre…)
                    qui servira de condition.
                </em>
            </div>
        );
    }

    return (
        <div className="cond-builder">
            <div className="cond-row">
                <label className="cond-field">
                    <span className="cond-label">Champ</span>
                    <select
                        value={rule?.fieldId ?? ''}
                        onChange={(e) => handleSourceChange(e.target.value)}
                    >
                        <option value="">— Choisir un champ —</option>
                        {fields.map((r) => (
                            <option key={r.field.id} value={r.field.id}>
                                {r.stepTitle} › {r.field.label || '(sans titre)'}
                            </option>
                        ))}
                    </select>
                </label>

                {sourceField && currentOp && (
                    <label className="cond-field">
                        <span className="cond-label">Opérateur</span>
                        <select
                            value={currentOp}
                            onChange={(e) => handleOpChange(e.target.value as ConditionOperator)}
                        >
                            {ops.map((op) => (
                                <option key={op} value={op}>{operatorLabel(op)}</option>
                            ))}
                        </select>
                    </label>
                )}
            </div>

            {sourceField && currentOp && (
                <ValuePicker
                    field={sourceField}
                    operator={currentOp}
                    rule={rule!}
                    onChange={onChange}
                />
            )}
        </div>
    );
}

/* ---------- Value picker ---------- */

interface ValuePickerProps {
    field: Field;
    operator: ConditionOperator;
    rule: VisibilityRule;
    onChange: (next: VisibilityRule) => void;
}

function ValuePicker({ field, operator, rule, onChange }: ValuePickerProps) {
    const setValue = (patch: Partial<VisibilityRule>) => {
        const next: VisibilityRule = { fieldId: rule.fieldId, ...patch };
        onChange(next);
    };

    const options = 'options' in field
        ? (field as { options: Array<{ id: string; label: string }> }).options
        : null;

    if ((operator === 'equals' || operator === 'notEquals' || operator === 'includes') && options) {
        const value = (rule.equals ?? rule.notEquals ?? rule.includes) as string | undefined;
        return (
            <div className="cond-value">
                <div className="cond-label">Valeur</div>
                <div className="cond-chips-pick">
                    {options.map((o) => {
                        const active = value === o.id;
                        return (
                            <button
                                key={o.id}
                                type="button"
                                className={`cond-chip${active ? ' is-active' : ''}`}
                                onClick={() => {
                                    if (operator === 'equals') setValue({ equals: o.id });
                                    else if (operator === 'notEquals') setValue({ notEquals: o.id });
                                    else setValue({ includes: o.id });
                                }}
                            >
                                {o.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    if ((operator === 'in' || operator === 'notIn') && options) {
        const values = (rule.in ?? rule.notIn ?? []).map(String);
        const toggle = (id: string) => {
            const next = values.includes(id) ? values.filter((v) => v !== id) : [...values, id];
            if (operator === 'in') setValue({ in: next });
            else setValue({ notIn: next });
        };
        return (
            <div className="cond-value">
                <div className="cond-label">Valeurs (sélection multiple)</div>
                <div className="cond-chips-pick">
                    {options.map((o) => {
                        const active = values.includes(o.id);
                        return (
                            <button
                                key={o.id}
                                type="button"
                                className={`cond-chip${active ? ' is-active' : ''}`}
                                onClick={() => toggle(o.id)}
                            >
                                {o.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (field.type === 'slider' || field.type === 'number') {
        const value = (rule.equals ?? rule.notEquals) as number | undefined;
        return (
            <div className="cond-value">
                <div className="cond-label">Valeur</div>
                <input
                    type="number"
                    className="cond-number"
                    value={value ?? ''}
                    onChange={(e) => {
                        const n = e.target.value === '' ? null : Number(e.target.value);
                        if (n === null) return;
                        if (operator === 'equals') setValue({ equals: n });
                        else setValue({ notEquals: n });
                    }}
                />
            </div>
        );
    }

    return null;
}

/* ---------- Helpers ---------- */

function inferOperator(rule: VisibilityRule): ConditionOperator | undefined {
    if (rule.equals !== undefined) return 'equals';
    if (rule.notEquals !== undefined) return 'notEquals';
    if (rule.in !== undefined) return 'in';
    if (rule.notIn !== undefined) return 'notIn';
    if (rule.includes !== undefined) return 'includes';
    return undefined;
}

function buildRule(
    fieldId: string,
    op: ConditionOperator,
    val: string | number | Array<string | number> | undefined
): VisibilityRule {
    const base: VisibilityRule = { fieldId };
    switch (op) {
        case 'equals':
            if (val !== undefined) base.equals = val as string | number;
            break;
        case 'notEquals':
            if (val !== undefined) base.notEquals = val as string | number;
            break;
        case 'in':
            base.in = (Array.isArray(val) ? val : []) as Array<string | number>;
            break;
        case 'notIn':
            base.notIn = (Array.isArray(val) ? val : []) as Array<string | number>;
            break;
        case 'includes':
            if (val !== undefined) base.includes = val as string;
            break;
    }
    return base;
}

function initialValueFor(
    field: Field,
    op: ConditionOperator
): string | number | Array<string | number> | undefined {
    const firstOpt =
        'options' in field
            ? (field as { options: Array<{ id: string }> }).options[0]?.id
            : undefined;

    if (op === 'in' || op === 'notIn') return [];
    if (field.type === 'slider' || field.type === 'number') return 0;
    return firstOpt;
}
